import { useDebouncedCallback } from '@mantine/hooks';
import { ComponentProps, useEffect, useState } from 'react';
import { LuSearch } from 'react-icons/lu';

import { Input } from '@/components/ui/input';
import { cn } from '@/utils/cn';

interface DebouncedSearchInputProps extends Omit<ComponentProps<typeof Input>, 'value' | 'defaultValue' | 'onChange'> {
    value: string;
    onValueChange: (value: string) => void;
    debounceMs?: number;
    containerClassName?: string;
}

// Local state keeps typing responsive even when applying the search is expensive.
export const DebouncedSearchInput = ({
    value,
    onValueChange,
    debounceMs = 300,
    containerClassName,
    className,
    ...inputProps
}: DebouncedSearchInputProps) => {
    const [draft, setDraft] = useState(value);
    const search = useDebouncedCallback(onValueChange, debounceMs);

    useEffect(() => {
        setDraft(value);
        search.cancel();
    }, [value, search]);

    return (
        <div className={cn('relative', containerClassName)}>
            <LuSearch className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-stone-500" />
            <Input
                aria-label="Search"
                placeholder="Search…"
                {...inputProps}
                className={cn('pl-9', className)}
                value={draft}
                onChange={(event) => {
                    const next = event.target.value;
                    setDraft(next);
                    search(next);
                }}
            />
        </div>
    );
};
